import type { ElementType, PropsWithChildren } from 'react';
import { useMemo } from 'react';

import { useSortable } from '@dnd-kit/react/sortable';
import { twMerge } from 'tailwind-merge';

import { SortableItemContext } from './context';

type SortableItemProps<T extends ElementType> = {
  id: string | number;
  index?: number;
  className?: string;
  as?: T;
} & Omit<React.ComponentProps<T>, 'id' | 'index' | 'className' | 'as' | 'children' | 'style' | 'ref'>;

export function SortableItem<T extends ElementType = 'li'>({
  children,
  className,
  id,
  index,
  as,
  ...props
}: PropsWithChildren<SortableItemProps<T>>) {
  const { ref, handleRef, isDragging } = useSortable({
    id,
    index: index ?? 0,
  });

  const context = useMemo(
    () => ({
      handleRef,
    }),
    [handleRef]
  );

  const Component = as || 'li';

  return (
    <SortableItemContext value={context}>
      <Component
        {...props}
        className={twMerge(className, 'flex flex-row items-end gap-1')}
        ref={ref}
        style={{ opacity: isDragging ? 0.4 : undefined }}
      >
        {children}
      </Component>
    </SortableItemContext>
  );
}
